import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import SimulatedBanner from "@/components/SimulatedBanner";
import PhotoUploader from "@/components/PhotoUploader";
import { ShoppingBag, Send, Clock, CheckCircle2, XCircle, Loader2, ImagePlus, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const STATUS_META = {
  pendiente_aprobacion: { label: "Pendiente aprobación", cls: "bg-brand-aqua-soft text-brand-aqua-dark border-brand-aqua/40", Icon: Clock },
  en_cola:              { label: "En cola",              cls: "bg-stone-100 text-stone-600 border-stone-300", Icon: Clock },
  programada:           { label: "Programada",           cls: "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/40", Icon: Clock },
  publicada:            { label: "Publicada",            cls: "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/40", Icon: CheckCircle2 },
  rechazada:            { label: "Rechazada",            cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
  fallida:              { label: "Fallida",              cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

export default function Marketplace() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketplace/items");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [items.length]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || title.trim().length < 2) return toast.error("Título muy corto");
    setSending(true);
    try {
      await api.post("/marketplace/items", {
        title: title.trim(),
        description: desc.trim(),
        price: parseFloat(price) || 0,
        currency: currency.toUpperCase(),
        photos: photos,
      });
      toast.success("Item agregado a la cola");
      setTitle(""); setDesc(""); setPrice(""); setPhotos([]);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const decide = async (item, action) => {
    try {
      await api.post(`/marketplace/items/${item.id}/decision`, { action });
      toast.success(action === "aprobar" ? "Aprobado" : "Rechazado");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="marketplace-page">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="h-1 w-16 bg-brand-aqua mb-4 rounded-full" />
          <h1 className="font-serif-display text-3xl sm:text-4xl text-brand-ink font-semibold">Facebook Marketplace</h1>
          <p className="text-stone-600 mt-2 max-w-2xl">
            <span>Cola de publicación asistida con intervalos aleatorios (comportamiento humano). </span>
            <span>Facebook no expone API oficial de Marketplace — modo asistido/simulado.</span>
          </p>
        </div>
        <SimulatedBanner testid="mp-simulated-banner" platform="marketplace" />
      </div>

      {/* Composer estilo chat */}
      <div className="card-elevated overflow-hidden flex flex-col" style={{ minHeight: "480px" }}>
        <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-stone-50/40" ref={listRef} data-testid="mp-queue-list">
          {loading ? (
            <div className="text-center py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-stone-500 text-sm">Sin items — envíe el primero para comenzar la cola.</div>
          ) : items.slice().reverse().map((it) => {
            const st = STATUS_META[it.status] || STATUS_META.en_cola;
            const StIcon = st.Icon;
            return (
              <div key={it.id} className="max-w-2xl mr-auto bg-white border border-border rounded-2xl rounded-tl-sm p-4 space-y-2" data-testid={`mp-item-${it.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="font-semibold text-brand-ink"><span>{it.title}</span></div>
                  <Badge className={`border font-semibold text-[10px] ${st.cls}`}>
                    <StIcon className="h-3 w-3 mr-1" />
                    <span>{st.label}</span>
                  </Badge>
                </div>
                {it.description && <p className="text-sm text-stone-600"><span>{it.description}</span></p>}
                {it.photos?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`mp-item-photos-${it.id}`}>
                    {it.photos.slice(0, 6).map((url, i) => (
                      <img key={i} src={absUrl(url)} alt="foto" className="h-14 w-14 rounded-md object-cover border border-border" />
                    ))}
                    {it.photos.length > 6 && (
                      <div className="h-14 w-14 rounded-md border border-border flex items-center justify-center text-xs text-stone-500 bg-stone-50">
                        +{it.photos.length - 6}
                      </div>
                    )}
                  </div>
                )}
                <div className="text-xs text-stone-500 flex flex-wrap gap-3 items-center">
                  <span>{it.currency} {it.price?.toFixed(2)}</span>
                  <span>· <span>{it.author_name}</span></span>
                  <span>· <span>{fmt(it.scheduled_at || it.created_at)}</span></span>
                </div>
                {it.status === "pendiente_aprobacion" && (
                  <div className="flex gap-2 pt-2 border-t border-stone-100">
                    <Button size="sm" className="btn-primary" onClick={() => decide(it, "aprobar")} data-testid={`mp-approve-${it.id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Aprobar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => decide(it, "rechazar")} data-testid={`mp-reject-${it.id}`}>
                      <XCircle className="h-4 w-4 mr-1" /> Rechazar
                    </Button>
                  </div>
                )}
                {it.simulated_post_id && (
                  <div className="text-xs text-brand-emerald-dark font-mono flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    <span>{it.simulated_post_id}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <form onSubmit={submit} className="border-t border-border p-4 space-y-3 bg-white" data-testid="mp-composer">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Input placeholder="Título del anuncio" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="mp-input-title" />
            <Input placeholder="Precio" type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="mp-input-price" />
            <Input placeholder="USD" value={currency} onChange={(e) => setCurrency(e.target.value)} maxLength={5} data-testid="mp-input-currency" />
          </div>
          <div className="flex gap-2">
            <Textarea placeholder="Descripción del anuncio…" value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} data-testid="mp-input-desc" />
            <Button type="submit" disabled={sending} className="btn-primary shrink-0 self-end" data-testid="mp-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">Fotos</div>
            <PhotoUploader
              endpoint="/marketplace/photos"
              value={photos}
              onChange={setPhotos}
              testid="mp-photos"
            />
          </div>
        </form>
      </div>
    </div>
  );
}
